'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Breadcrumbs, Header, useCart } from '@/components';
import ArrowNarrowRightIcon from '@/components/icons/ArrowNarrowRightIcon';
import { categoryCoverKey, categoryImageSrc, categoryLabel } from '@/lib/search-hero';
import { getStoredStoreId } from '@/lib/store-storage';
import { DEFAULT_CATEGORY_TILES } from '@/lib/category-tiles';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';
const REQUEST_TIMEOUT_MS = 4000;

type CategoryTile = {
  title: string;
  image: string;
  link: string;
};

type Store = {
  covaLocationId: number;
  name: string;
};

/** Category tiles the admin panel curates, shown first with their own artwork. */
type FeaturedTile = CategoryTile & { featured: true };
/** Everything else the selected store actually stocks. */
type LiveTile = { title: string; link: string; featured: false };

async function fetchJson<T>(url: string, fallback: T): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(url, { cache: 'no-store', signal: controller.signal });
    if (!res.ok) throw new Error(String(res.status));
    return (await res.json()) as T;
  } catch {
    return fallback;
  } finally {
    clearTimeout(timeoutId);
  }
}

/** `/search?category=Flower` → `flower`, so a curated tile and a live category dedupe. */
function categoryKeyFromLink(link: string): string {
  try {
    const query = link.includes('?') ? link.slice(link.indexOf('?')) : '';
    const value = new URLSearchParams(query).get('category');
    return (value || '').trim().toLowerCase();
  } catch {
    return '';
  }
}

export default function CategoriesPage() {
  // Fixed list; see lib/category-tiles.ts.
  const featured: FeaturedTile[] = DEFAULT_CATEGORY_TILES.map((c) => ({ ...c, featured: true }));
  const [liveCategories, setLiveCategories] = useState<string[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const { state } = useCart();

  const storeId = state.storeCovaLocationId || getStoredStoreId();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      const storesData = await fetchJson<{ stores?: Store[] }>(`${API_BASE}/api/stores`, {});

      if (cancelled) return;
      setStores(storesData?.stores || []);

      // The live list is per store, so a shopper never sees a category their
      // store cannot fill.
      if (storeId) {
        const products = await fetchJson<{ categories?: string[] }>(
          `${API_BASE}/api/store/${storeId}/products?limit=1`,
          {}
        );
        if (!cancelled) setLiveCategories(products?.categories || []);
      } else if (!cancelled) {
        setLiveCategories([]);
      }

      if (!cancelled) setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  const tiles = useMemo(() => {
    const seen = new Set(featured.map((tile) => categoryKeyFromLink(tile.link)).filter(Boolean));
    const rest: LiveTile[] = liveCategories
      .filter((name) => !seen.has(name.trim().toLowerCase()))
      .map((name) => ({
        title: categoryLabel(name) || name,
        link: `/search?category=${encodeURIComponent(name)}`,
        featured: false,
      }));
    return [...featured, ...rest];
  }, [featured, liveCategories]);

  return (
    <div className="min-h-screen">
      <Header stores={stores} base={API_BASE} />
      <main className="home-page w-full max-w-none px-4 pb-16 pt-4 sm:px-8 sm:pt-6">
        {/* Hero band, matching /collections and the /search shop hero */}
        <div className="relative overflow-hidden rounded-xl bg-brand-100 sm:rounded-2xl">
          <div className="absolute right-0 top-0 hidden h-full w-1/2 lg:block">
            <Image
              src="/media/categories/covers/cover-all.webp"
              alt=""
              fill
              sizes="(min-width: 1024px) 50vw, 100vw"
              className="object-cover object-right"
              priority
            />
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(180deg, rgba(23, 22, 90, 0) 0%, #17165A 111.31%)' }}
            />
          </div>
          <div className="absolute inset-0 lg:hidden">
            <Image
              src="/media/categories/covers/cover-all.webp"
              alt=""
              fill
              sizes="100vw"
              className="object-cover object-right"
              priority
            />
            <div className="absolute inset-0 bg-brand-100/80" />
          </div>

          <div className="relative p-4 lg:p-6">
            <Breadcrumbs items={[{ label: 'Categories' }]} tone="onDark" className="mb-0" />
            <h1 className="mt-3 text-h3-mobile font-bold text-constant-white lg:text-h3-desktop">
              Shop by category
            </h1>
            <p className="mt-2 max-w-3xl text-body-14-mobile font-medium text-constant-white/80 lg:text-body-16">
              Every category we carry, from flower and pre-rolls to edibles, vapes and the gear that
              goes with them. Pick one to jump straight into that part of the menu.
            </p>
            {!loading && tiles.length > 0 && (
              <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-constant-white/60">
                {tiles.length} categor{tiles.length === 1 ? 'y' : 'ies'}
              </p>
            )}
          </div>
        </div>

        <section className="mt-6 w-full">
          {loading ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={`category-skeleton-${index}`}
                  className="h-[220px] w-full animate-pulse rounded-2xl border border-neutral-10 bg-neutral-5"
                />
              ))}
            </div>
          ) : tiles.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {tiles.map((tile) => {
                const src = tile.featured
                  ? tile.image
                  : categoryImageSrc(categoryCoverKey(tile.title));

                return (
                  <Link
                    key={`${tile.title}-${tile.link}`}
                    href={tile.link}
                    aria-label={`Shop ${tile.title}`}
                    className="group relative block h-[220px] w-full overflow-hidden rounded-2xl border border-neutral-10"
                  >
                    <Image
                      src={src}
                      alt=""
                      fill
                      className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                    <div
                      className="pointer-events-none absolute inset-0"
                      style={{
                        background:
                          'linear-gradient(180deg, rgba(23,22,90,0.15) 0%, rgba(23,22,90,0.25) 55%, rgba(23,22,90,0.78) 100%)',
                      }}
                    />

                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-5">
                      <h2 className="min-w-0 truncate text-body-20 font-bold leading-snug text-constant-white">
                        {tile.title}
                      </h2>
                      <span
                        aria-hidden
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-constant-white/30 bg-constant-white/15 text-constant-white backdrop-blur-sm transition-colors duration-200 group-hover:border-constant-white group-hover:bg-constant-white group-hover:text-blue-100"
                      >
                        <ArrowNarrowRightIcon size={18} color="currentColor" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-neutral-10 bg-constant-white px-6 py-14 text-center shadow-sm">
              <p className="text-body-16 font-semibold text-text-primary">No categories here yet</p>
              <p className="mx-auto mt-2 max-w-md text-body-14-desktop text-text-secondary">
                Pick a store from the header and we&apos;ll show you what it stocks.
              </p>
              <Link
                href="/search"
                className="mt-6 inline-flex h-11 items-center justify-center rounded-xl border border-blue-100 bg-blue-100 px-6 text-sm font-semibold text-constant-white transition-colors hover:bg-blue-80"
              >
                Shop all products
              </Link>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
